<template>
  <div>
    <v-text-field id="multiselect-search" v-if="options.search && items.length > 10" v-model="search"
      :placeholder="placeholder" filled clearable></v-text-field>
    <v-list id="choices" nav class="pa-0" :class="this.cardClass()">
      <v-list-item v-for="(item, index) in sortedItems" :key="item.id" :ripple="false" @click="select(item.id)"
        :id="'response-' + item.name" :class="listClass(item)" class="ps-5 mb-0 list-item-with-divider">
        <v-list-item-avatar v-if="useAvatar" :color="avatarColor(item)" size="30">
        </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title class="text-wrap response">
            <div v-if="$t(title(item)).split('\n').length > 1">
              <div v-for="(text, index) in $t(title(item)).split('\n')" :key="index">
                {{ text }}
              </div>
            </div>
            <div v-else>
              <mrx-translation :multiSelect="tokenIsMultiselect" :token="title(item)"></mrx-translation>
            </div>
          </v-list-item-title>
          <v-list-item-subtitle v-if="hasDetails(item)" class="pre-wrap">
            <div v-if="$t(details(item)).split('\n').length > 1">
              <div v-for="(text, index) in $t(details(item)).split('\n')" :key="index" class="mb-1">
                {{ text }}
              </div>
            </div>
            <div v-else>
              <mrx-translation :cssClasses="['subheading']" :token="details(item)" class="text-wrap"></mrx-translation>
            </div>
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action class="my-0">
          <v-btn icon>
            <v-icon size="30">
              {{ item.selected ? selector.on : selector.off }}
            </v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
import languageLogic from '@/mixins/languageLogic'
import tokens from '@/mixins/tokens'
import icons from '@/mixins/icons'
import ui from '@/mixins/ui'
import { mapState } from 'pinia'
import { useTestModeStore } from '@/stores/testMode'
import { usePartnerConfigStore } from '@/stores/partnerConfig'
import { useNavigation } from '@/composables/useNavigation';
import { createDebouncedTracker } from '@/plugins/analytics/helpers'

export default {
  mixins: [languageLogic, icons, tokens, ui],
  name: 'multiselect',
  model: {
    prop: 'items',
    event: 'click'
  },
  data() {
    return {
      value: [],
      search: null,
    }
  },
  created() {
    this.trackSearchResults = createDebouncedTracker((event, props) => {
      this.$track(event, props)
      if (props.results_count === 0) {
        this.$track(this.$analyticsEvents.SEARCH_NO_RESULTS, props)
      }
    }, 600)
  },
  props: {
    items: Array,
    options: { type: Object, default: () => ({}) },
    panelName: String,
    panelType: String,
    disabled: Boolean,
    onClick: { type: Function, default: () => { } },
    languagePath: { type: String, default: 'responses' },
    sortAlphabetical: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
  },
  setup() {
    const { next } = useNavigation();
    return { next };
  },
  methods: {
    avatarColor(item) {
      if (this.panelHasColorAvatar(this.panelName)) {
        return this.getAvatarColor(this.panelName, item.value)
      } else {
        return this.skinTone
      }
    },
    select(id) {
      if (this.disabled) {
        return
      }
      this.$emit('selectEvent')
      const item = this.items.find(i => i.id === id)
      const wasSelected = item.selected
      if (this.panelType === 'multiple') {
        this.handleMultipleSelect(id)
      }
      else {
        this.handleSingleSelect(id)
      }
      this.$track(this.$analyticsEvents.MULTISELECT_OPTION_SELECTED, {
        component: 'MultiSelect',
        panel_name: this.panelName,
        panel_type: this.panelType,
        option_value: item.value,
        option_name: item.name,
        option_text: this.$t(this.title(item)),
        is_exclusive: this.isExclusiveResponse(id),
        was_selected: wasSelected,
        now_selected: item.selected,
        action: wasSelected ? 'deselected' : 'selected',
        auto_progressed: (this.getMode('oneClickPanels') || this.autoProgress) && this.selected
      })
    },
    handleMultipleSelect(id) {
      if (this.isExclusiveResponse(id)) {
        this.items.forEach(response => {
          if (response.id === id) {
            response.selected = !response.selected
          }
          else {
            response.selected = false
          }
        })

        if (!this.embedded && this.autoProgress && this.selected) {
          this.next()
        }
      }
      else {
        this.items.forEach(response => {
          if (this.isExclusiveResponse(response.id)) {
            response.selected = false
          }
          else if (response.id === id) {
            response.selected = !response.selected
          }
        })
      }
    },
    isExclusiveResponse(id) {
      if (this.options.firstResponseClears && this.items[0].id === id) {
        return true;
      }
      const selectedResponse = this.items.find(item => item.id === id);
      const defaultExclusive = ['none_above', 'unknown', 'normal'];
      return defaultExclusive.includes(selectedResponse.value);
    },
    handleSingleSelect(id) {
      this.items.forEach(item => {
        if (item.id === id) {
          this.onClick(item.value)
          item.selected = true;
        } else {
          item.selected = false;
        }
      })
      if (!this.embedded && (this.getMode('oneClickPanels') || this.autoProgress) && this.selected) {
        this.next()
      }
    },
    listClass(item) {
      var classes = ""

      // preference panels keep the dark theme even once a patient uuid exists
      if (this.$router.currentRoute.name === 'Preference') {
        classes = "intro-dark"
      } else {
        classes = this.cardClass()
      }

      if (item.selected) {
        classes = classes.concat(" selected-item")
      }

      return classes
    },
    title(item) {
      let { token } = item;

      if (!token) {
        if (this.languagePath === 'tracks') {
          token = `tracks.${item.avatar || item.name || item.path}.title`;
        } else if (this.languagePath === 'streamlined') {
          token = `responses.${item.name}.title`;
        } else {
          token = this.responseToken(item);
        }
      }

      return token;
    },
    details(item) {
      return `${this.languagePath}.${item.avatar || item.name || item.path}.details`
    },
    hasDetails(item) {
      return true // this.$te(this.details(item), this.$i18n.fallbackLocale)
    },
  },
  computed: {
    useAvatar() {
      return this.panelHasColorAvatar(this.panelName)
    },
    tokenIsMultiselect() {
      return this.panelType === 'multiple'
    },
    placeholder() {
      return this.$t('app.inputs.search.title')
    },
    cssClass() {
      if (this.disabled) {
        return 'mb-0 grey lighten-2'
      }
      else {
        return 'mb-0 grey multiselect'
      }
    },
    sortedItems() {
      let sortFunc;

      if (this.sortAlphabetical) {
        sortFunc = (a, b) => {
          const localizedA = this.$t(this.title(a));
          const localizedB = this.$t(this.title(b));

          return localizedA.localeCompare(localizedB);
        }
      } else {
        sortFunc = (a, b) => (a.ordering - b.ordering);
      }

      const sorted = this.items.sort(sortFunc);

      return this.search ?
        sorted.filter(item => this.$t(this.title(item)).match(new RegExp(this.search, 'i'))) :
        sorted
    },
    selected() {
      return this.items.some(x => x.selected)
    },
    selector() { return (this.panelType === 'multiple') ? { on: this.icons.mdiCheckboxOutline, off: this.icons.mdiCheckboxBlankOutline } : { on: this.icons.mdiCheckCircleOutline, off: this.icons.mdiCheckboxBlankCircleOutline } },
    ...mapState(useTestModeStore, { getMode: 'get' }),
    ...mapState(usePartnerConfigStore, ['skinTone', 'autoProgress']),
  },

  watch: {
    panelName: {
      handler() {
        this.search = null
      }
    },
    search: {
      handler(newValue) {
        if (newValue && newValue.length > 0) {
          this.trackSearchResults(this.$analyticsEvents.MULTISELECT_SEARCH_USED, {
            component: 'MultiSelect',
            panel_name: this.panelName,
            results_count: this.sortedItems.length
          })
        }
      }
    }
  }
}
</script>

<style scoped>
img {
  width: 100%;
}

.line-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pre-wrap {
  white-space: pre-wrap;
}

.list-item-with-divider:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

@media (pointer: coarse) {
  .v-list-item:not(.selected-item)::before,
  .v-list-item:not(.selected-item) .v-list-item__action .v-btn::before 
  {
    background-color: inherit;
  } 
}
</style>
